import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Header } from "./IndustriesPanel";

export default function LeadsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | new | contacted
  const [updatingId, setUpdatingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    const total = items.length;
    const contacted = items.filter((l) => l.contacted).length;
    return { total, contacted, open: total - contacted };
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "new") return items.filter((l) => !l.contacted);
    if (filter === "contacted") return items.filter((l) => l.contacted);
    return items;
  }, [items, filter]);

  const toggleContacted = async (lead, value) => {
    setUpdatingId(lead.id);
    // optimistic update
    setItems((prev) =>
      prev.map((l) => (l.id === lead.id ? { ...l, contacted: value } : l))
    );
    try {
      const { data } = await api.patch(`/admin/leads/${lead.id}`, { contacted: value });
      setItems((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...data } : l)));
      toast.success(value ? "Marked as contacted." : "Marked as not contacted.");
    } catch (e) {
      toast.error(formatApiError(e));
      // revert
      setItems((prev) =>
        prev.map((l) => (l.id === lead.id ? { ...l, contacted: !value } : l))
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const saveNotes = async (lead, notes) => {
    if ((notes || "") === (lead.notes || "")) return;
    try {
      const { data } = await api.patch(`/admin/leads/${lead.id}`, { notes });
      setItems((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...data } : l)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      setItems((prev) => prev.filter((l) => l.id !== lead.id));
      toast.success("Lead deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="leads-panel" className="space-y-6">
      <Header
        title="Leads"
        subtitle={`Demo requests captured from the website (${stats.total}). ${stats.open} open · ${stats.contacted} contacted.`}
      />

      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: "all", label: `All · ${stats.total}` },
          { id: "new", label: `Not contacted · ${stats.open}` },
          { id: "contacted", label: `Contacted · ${stats.contacted}` },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`leads-filter-${t.id}`}
            onClick={() => setFilter(t.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              filter === t.id
                ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
                : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <Th className="w-24">Contacted</Th>
              <Th className="w-32">Date</Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Company</Th>
              <Th>Use case</Th>
              <Th>Message</Th>
              <Th>Notes</Th>
              <Th className="w-16"></Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="p-4 text-sm text-neutral-500">Loading…</td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="p-6 text-sm text-neutral-500">
                  {items.length === 0 ? "No leads yet." : "No leads in this filter."}
                </td>
              </tr>
            )}
            {filtered.map((l) => (
              <tr
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                className={`border-b border-neutral-100 last:border-0 align-top ${
                  l.contacted ? "bg-neutral-50/50" : ""
                }`}
              >
                <Td>
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid={`lead-contacted-${l.id}`}
                      checked={Boolean(l.contacted)}
                      disabled={updatingId === l.id}
                      onChange={(e) => toggleContacted(l, e.target.checked)}
                      className="w-4 h-4 accent-brand"
                    />
                    <span className={`text-xs font-medium ${l.contacted ? "text-brand-dark" : "text-neutral-500"}`}>
                      {l.contacted ? "Yes" : "No"}
                    </span>
                  </label>
                  {l.contacted && l.contacted_at && (
                    <div className="font-mono-ui text-[9px] uppercase tracking-[0.18em] text-neutral-400 mt-1">
                      {(l.contacted_at || "").slice(0, 10)}
                    </div>
                  )}
                </Td>
                <Td className="font-mono-ui text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                  {(l.created_at || "").slice(0, 19).replace("T", " ")}
                </Td>
                <Td className="font-medium">{l.name}</Td>
                <Td>
                  <a className="text-neutral-900 underline underline-offset-2" href={`mailto:${l.email}`}>
                    {l.email}
                  </a>
                </Td>
                <Td>{l.company || "-"}</Td>
                <Td className="text-xs">{l.use_case || "-"}</Td>
                <Td className="max-w-xs text-neutral-600 text-xs">{l.message || "-"}</Td>
                <Td>
                  <input
                    type="text"
                    data-testid={`lead-notes-${l.id}`}
                    defaultValue={l.notes || ""}
                    placeholder="Internal notes…"
                    onBlur={(e) => saveNotes(l, e.target.value)}
                    className="w-full text-xs px-2 py-1.5 rounded-md border border-neutral-200 focus:border-brand focus:outline-none"
                  />
                </Td>
                <Td>
                  <button
                    type="button"
                    data-testid={`lead-delete-${l.id}`}
                    onClick={() => remove(l)}
                    title="Delete lead"
                    className="text-xs font-semibold text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50"
                  >
                    Delete
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-left font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 px-3 py-2 ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2 ${className}`}>{children}</td>
);
